/**
 * The BPM bucket table (venue-category-discovery arch §4.0, PRD D-31).
 *
 * ONE fixed table maps a tempo to a day part. It replaced two mechanisms that
 * both walked a venue category's BPM curve — the curator's day-part suggestion
 * and the auto-schedule ranking — because the three parts tile the whole
 * circular day (ADR-029), so the "middle of the evening window" of a bar landed
 * at 05:30 and of a night club at 08:45: every category was characterised by the
 * hour it is most reliably shut. A table has no window, no midpoint and no
 * curve, so that failure mode does not exist.
 *
 * FOUR READERS, ONE HOME: the showcase suggestion (§4.1.3), the auto-schedule
 * ranking (§4.3), the admin card — which prints the bucket's own edges when it
 * explains WHY «Вечер» was proposed — and the worker's energy auto-tagging.
 *
 * THE WORKER IS WHY `energyTagName` IS HERE. `EnergyAutoTagService`
 * (BUG-160 / BUG-195) already maps an ingested track's BPM to an energy tag on
 * exactly these cut points — `<105 → Low`, `≤117 → Medium`, `≤124 → High`,
 * `≥125 → Peak`, plus `Beatless`. The CEO's day-part table IS that mapping read
 * differently, so a second copy of 105 / 117 / 124 / 125 would be a drift risk
 * between what a track is TAGGED and what a preset is PROPOSED FOR.
 */
export const BPM_BUCKETS = [
    {
        code: 'beatless',
        bpmMin: null,
        bpmMax: null,
        dayPart: 'morning',
        isPeak: false,
        energyTagName: 'Beatless',
    },
    {
        code: 'slow',
        bpmMin: 60,
        bpmMax: 104,
        dayPart: 'morning',
        isPeak: false,
        energyTagName: 'Low',
    },
    {
        code: 'medium',
        bpmMin: 105,
        bpmMax: 117,
        dayPart: 'day',
        isPeak: false,
        energyTagName: 'Medium',
    },
    {
        code: 'fast',
        bpmMin: 118,
        bpmMax: 124,
        dayPart: 'evening',
        isPeak: false,
        energyTagName: 'High',
    },
    {
        code: 'peak',
        bpmMin: 125,
        bpmMax: 130,
        dayPart: 'evening',
        isPeak: true,
        energyTagName: 'Peak',
    },
];
export const BPM_BUCKET_CODES = BPM_BUCKETS.map((bucket) => bucket.code);
export const BPM_BUCKETS_BY_CODE = Object.fromEntries(BPM_BUCKETS.map((bucket) => [bucket.code, bucket]));
/**
 * Resolve a tempo to its bucket.
 *
 * TOTAL ON THE REALS, AND THAT IS THE LOAD-BEARING DETAIL. Production BPM runs
 * 79–163; a table read as a closed 60–130 range would return nothing for a real
 * 163 and the day part would vanish with no error — precisely the `parseBpmCurve`
 * silent drop that hid BUG-283 for months. The printed edges above are the
 * LABELS a curator reads; the resolver clamps at both ends by construction.
 *
 * `beatless` is deliberately unreachable from a number: «без бита» is decided by
 * the explicit `BL` filename marker at ingest, never inferred from an absent or
 * odd tempo (BUG-195 removed exactly that inference).
 */
export function resolveBpmBucket(bpm) {
    if (bpm < 105)
        return 'slow';
    if (bpm <= 117)
        return 'medium';
    if (bpm <= 124)
        return 'fast';
    return 'peak';
}
/** The day part a tempo belongs to — the one-line answer §4.1.3 needs. */
export function resolveBpmDayPart(bpm) {
    return BPM_BUCKETS_BY_CODE[resolveBpmBucket(bpm)].dayPart;
}
/**
 * Centre of the union of the ranges that map to each part — morning 82, day 111,
 * evening 124. Used by the auto-schedule ranking (§4.3) as the target a
 * candidate preset's `bpm_median` is compared against.
 *
 * DERIVED from `BPM_BUCKETS`, never a second literal: the moment these numbers
 * are written twice they are free to drift, which is the whole reason this
 * module exists.
 */
export const PART_TARGET_BPM = (() => {
    const parts = ['morning', 'day', 'evening'];
    const entries = parts.map((part) => {
        const ranged = BPM_BUCKETS.filter((bucket) => bucket.dayPart === part && bucket.bpmMin !== null && bucket.bpmMax !== null);
        const min = Math.min(...ranged.map((bucket) => bucket.bpmMin));
        const max = Math.max(...ranged.map((bucket) => bucket.bpmMax));
        return [part, Math.round((min + max) / 2)];
    });
    return Object.fromEntries(entries);
})();
//# sourceMappingURL=bpm-buckets.js.map